import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { PersonsProvider, TagsProvider } from '@modeso/aumico-lib-companies-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { combineLatest, filter } from 'rxjs';
import { TasksOverviewActions } from '../../../../../../aumico-lib-cpmm-fe/src/lib';
import { SortDirection } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { TaskState } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/task-state.enum';
import {
    PersonProjectDto,
    PersonProjectsActions,
    PersonProjectSortBy,
    PersonProjectsFilter,
    PersonProjectTableValue,
    selectPersonProjects,
} from '../../../../../../aumico-lib-projects-fe/src/lib';
import { activeNavigation } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/activeNavigation.enum';
import { ProjectStatus } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/project-status.enum';
import { AssigneeFilter } from '../../../enums/assigneeFilter.enum';
import { Paths } from '../../../enums/paths.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { lastProjectYearByPersonId, toPersonProjectTableData } from '../../ms-annual-reports/helpers/person-projects-table.helper';

/**
 * REV-22155 — tab "Projekte" of a person, like the company's `company-projects-overview`: the person
 * projects of this person, without the "Person" column; "+ Neues Projekt" with the person preset.
 * An inactive person gets no new projects; the existing ones stay visible and editable.
 */
@UntilDestroy()
@Component({
    selector: 'app-person-projects-page',
    templateUrl: 'person-projects.page.html',
    styleUrls: ['../../ms-companies/company-projects-overview/company-projects-overview.page.scss'],
})
export class PersonProjectsPage extends BasePageComponent implements OnInit {
    readonly activeNav = activeNavigation.COMPANY;
    personId = '';
    person: IPersonDTO | null = null;
    filter: PersonProjectsFilter = {};
    tableData: PersonProjectTableValue[] = [];
    assigneeOptions: { id: string; name: string }[] = [];
    lastProjectYearByPersonId: Record<string, number> = {};
    showCreateProjectPopup = false;
    selectedProjects = new Set<string>();
    allProjectsSelected = false;
    readonly bulkActions = [{ id: 'STATUS_CHANGE', translationKey: 'aumico_project_status_change_action_bar' }];
    readonly deleteAction = { id: 'DELETE_PROJECTS', translationKey: 'aumico_project_delete_action_bar' };
    openBulkEditStatus = false;
    openBulkDeleteConfirmationDialog = false;
    openAutoTaskUpdateDialog = false;
    readonly possibleStatus = [
        { id: ProjectStatus.ACTIVE, name: this.i18next.t('aumico_projects_status_active') as string },
        { id: ProjectStatus.DONE, name: this.i18next.t('aumico_projects_status_done') as string },
    ];
    readonly bulkEditStatusControl = this.fb.control<{ id: ProjectStatus; name: string } | null>(null);

    readonly statusOptions = [
        { label: this.i18next.t('aumico_projects_status_active') as string, value: ProjectStatus.ACTIVE },
        { label: this.i18next.t('aumico_projects_status_done') as string, value: ProjectStatus.DONE },
        { label: this.i18next.t('aumico_projects_status_archived') as string, value: ProjectStatus.ARCHIVED },
    ];

    private projects: PersonProjectDto[] = [];

    constructor(
        injector: Injector,
        private readonly i18next: I18NextService,
        private readonly store: Store,
        private readonly personsProvider: PersonsProvider,
        private readonly tagsProvider: TagsProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly fb: FormBuilder,
    ) {
        super(injector);
    }

    get isPersonActive(): boolean {
        return this.person?.active !== false;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        // The details page above reads `:personId`; this tab is its child.
        this.personId = this.activatedRoute.parent?.snapshot.params['personId'] ?? this.activatedRoute.snapshot.params['personId'];

        this.organizationProvider.dispatchGetUsers(true);
        this.personsProvider.dispatchPersonsList();
        this.tagsProvider.dispatchTagsList();
        this.reload();

        this.personsProvider
            .getSelectedPerson$()
            .pipe(
                filter((person): person is IPersonDTO => person != null && person._id === this.personId),
                untilDestroyed(this),
            )
            .subscribe((person) => (this.person = person));

        combineLatest([
            this.store.select(selectPersonProjects),
            this.organizationProvider.getUsers(),
            this.personsProvider.getPersons$(),
            this.tagsProvider.getTagNamesById$(),
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([projects, users, persons, tagNamesById]) => {
                // The store also serves the projects page: keep this person's projects only.
                this.projects = projects.filter((p) => p.personId === this.personId);
                this.tableData = toPersonProjectTableData(
                    this.projects,
                    users,
                    persons,
                    tagNamesById,
                    this.i18next.t('aumico_tasks_unassigned') as string,
                );
                this.lastProjectYearByPersonId = lastProjectYearByPersonId(this.projects);
                this.assigneeOptions = [
                    { id: AssigneeFilter.unassigned, name: this.i18next.t('aumico_tasks_unassigned') as string },
                    ...users
                        .filter((u) => !u.isDeleted && !u.isInvitee)
                        .map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` })),
                ];
            });

        this.actionsListener$
            .pipe(ofType(PersonProjectsActions.personProjectCreated), untilDestroyed(this))
            .subscribe(({ project }) => {
                this.showCreateProjectPopup = false;
                this.openMandateManagement(project.projectId);
            });

        this.actionsListener$
            .pipe(ofType(TasksOverviewActions.taskStatusForProjectByTaskIdUpdated), untilDestroyed(this))
            .subscribe(() => this.reload());
    }

    onYearFilterChange(years: number[]): void {
        this.updateFilter({ years: years.length ? years : undefined });
    }

    onAssigneeFilterChange(userIds: string[]): void {
        this.updateFilter({ assignee: userIds.length ? userIds : undefined });
    }

    onStatusFilterChange(status: ProjectStatus[]): void {
        this.updateFilter({ projectStatus: status.length ? status : undefined });
    }

    sortProjects({ sortBy, sortOrder }: { sortBy: PersonProjectSortBy | null; sortOrder: SortDirection | null }): void {
        this.updateFilter({
            sortBy: sortBy ?? undefined,
            sortOrder: sortOrder === SortDirection.DESC ? 'DESC' : sortOrder === SortDirection.ASC ? 'ASC' : undefined,
        });
    }

    get activeSortOrder(): SortDirection | null {
        return this.filter.sortOrder === 'DESC' ? SortDirection.DESC : this.filter.sortOrder === 'ASC' ? SortDirection.ASC : null;
    }

    isAnyFilterActive(): boolean {
        return !!(this.filter.years?.length || this.filter.assignee?.length || this.filter.projectStatus?.length);
    }

    onCreateProject({ personId, year }: { personId: string; year: number }): void {
        this.store.dispatch(PersonProjectsActions.createPersonProject({ personId, year }));
    }

    onSelectProject({ project, section }: { project: PersonProjectTableValue; section?: string }): void {
        this.openMandateManagement(project.projectId, section);
    }

    onUpdateProjectStatus({ projectId, status, updateTasks }: { projectId: string; status: ProjectStatus; updateTasks: boolean }): void {
        this.store.dispatch(
            PersonProjectsActions.updatePersonProjectStatus({
                projectId,
                status,
                updateTasks,
                mandateManagementTaskIds: updateTasks ? this.openTaskIds([projectId]) : [],
            }),
        );
    }

    onActionClicked(actionId: string): void {
        if (actionId === 'STATUS_CHANGE') {
            this.bulkEditStatusControl.setValue(null);
            this.openBulkEditStatus = true;
        } else if (actionId === 'DELETE_PROJECTS') {
            this.openBulkDeleteConfirmationDialog = true;
        }
    }

    editStatusSelected(): void {
        const status = this.bulkEditStatusControl.value?.id;
        if (status == null) {
            return;
        }
        this.openBulkEditStatus = false;
        if (status === ProjectStatus.DONE) {
            this.openAutoTaskUpdateDialog = true;
        } else {
            this.bulkUpdateStatus(status, false);
        }
    }

    onEditStatusConfirm(updateTasks: boolean): void {
        this.openAutoTaskUpdateDialog = false;
        this.bulkUpdateStatus(ProjectStatus.DONE, updateTasks);
    }

    confirmDeleteBulkProjects(): void {
        this.openBulkDeleteConfirmationDialog = false;
        this.store.dispatch(PersonProjectsActions.bulkDeletePersonProjects({ projectIds: Array.from(this.selectedProjects) }));
        this.clearSelection();
    }

    private bulkUpdateStatus(status: ProjectStatus, updateTasks: boolean): void {
        const projectIds = Array.from(this.selectedProjects);
        this.store.dispatch(
            PersonProjectsActions.bulkUpdatePersonProjectStatus({
                projectIds,
                status,
                updateTasks,
                mandateManagementTaskIds: updateTasks ? this.openTaskIds(projectIds) : [],
            }),
        );
        this.clearSelection();
    }

    private openTaskIds(projectIds: string[]): string[] {
        return this.projects
            .filter((p) => projectIds.includes(p.projectId))
            .flatMap((p) => p.workflowData ?? [])
            .flatMap((w) => w.issuesDetails)
            .filter((i) => i.state !== TaskState.DONE && i.state !== TaskState.NOT_APPLICABLE)
            .map((i) => i.issueId);
    }

    private clearSelection(): void {
        this.selectedProjects = new Set();
        this.allProjectsSelected = false;
    }

    onDeleteProject(projectId: string): void {
        this.store.dispatch(PersonProjectsActions.deletePersonProject({ projectId }));
    }

    onChangeTaskState({ projectId, taskId, taskState }: { projectId: string; taskId: string; taskState: TaskState }): void {
        this.store.dispatch(
            TasksOverviewActions.updateTaskStatusForProjectByTaskId({ projectId, taskId, updateTaskDto: { taskState } }),
        );
    }

    onToggleCheckAllProject(): void {
        this.allProjectsSelected = !this.allProjectsSelected;
        this.selectedProjects = this.allProjectsSelected ? new Set(this.tableData.map((p) => p.projectId)) : new Set();
    }

    onToggleCheckProject(projectId: string): void {
        const next = new Set(this.selectedProjects);
        if (next.has(projectId)) {
            next.delete(projectId);
        } else {
            next.add(projectId);
        }
        this.selectedProjects = next;
        this.allProjectsSelected = next.size > 0 && next.size === this.tableData.length;
    }

    private updateFilter(changes: Partial<PersonProjectsFilter>): void {
        this.filter = { ...this.filter, ...changes };
        this.reload();
    }

    private reload(): void {
        this.store.dispatch(
            PersonProjectsActions.loadPersonProjects({ filter: { ...this.filter, personIds: [this.personId] } }),
        );
    }

    private openMandateManagement(projectId: string, section?: string): void {
        void this.router.navigate(
            [
                `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.PROJECT}`,
                Paths.PERSON_PROJECTS,
                projectId,
                Paths.MANDATE_MANAGEMENT,
            ],
            section != null ? { queryParams: { section } } : {},
        );
    }
}
